import React, { useState, useEffect } from "react";
import { Users, Briefcase, Award, Heart, Mail, GraduationCap, TrendingUp, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import AOS from "aos";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "../components/SEO";
import Hero from "../components/Hero";
import API from "../api";
import dayjs from "dayjs";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const AlumniPage = () => {
    const [activeTab, setActiveTab] = useState("alumni");

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const heroSlides = [
        {
            id: "alumni",
            image: "/assets/img/prog-1.jpg",
            title: "Alumni Community",
            subtitle: "Join a global network of accomplished graduates making a difference worldwide.",
            buttonText: "Connect With Us",
            buttonLink: "#alumni-section",
        },
    ];

    const benefits = [
        {
            icon: Briefcase,
            title: "Career Services",
            description: "Access exclusive job postings, career counseling, and professional development resources.",
        },
        {
            icon: Users,
            title: "Networking Events",
            description: "Connect with fellow alumni through regional meetups, webinars, and annual reunions.",
        },
        {
            icon: TrendingUp,
            title: "Mentorship Programs",
            description: "Mentor current students or receive guidance from experienced professionals in your field.",
        },
        {
            icon: Award,
            title: "Exclusive Discounts",
            description: "Enjoy special rates on continuing education courses, events, and university facilities.",
        },
        {
            icon: Globe,
            title: "Global Network",
            description: "Join alumni chapters worldwide and expand your professional connections internationally.",
        },
        {
            icon: Heart,
            title: "Give Back",
            description: "Support scholarships, research initiatives, and programs that shape future generations.",
        },
    ];

    const [featuredAlumni, setFeaturedAlumni] = useState([]);
    const [featuredLoading, setFeaturedLoading] = useState(true);

    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [eventsError, setEventsError] = useState(null);

    

    // Fetch featured alumni
    useEffect(() => {
        let mounted = true;
        const fetchFeaturedAlumni = async () => {
            try {
                setFeaturedLoading(true);
                const response = await API.get('/alumni/featured');
                if (mounted) {
                    const alumniData = response.data.data || [];
                    // Format the data to match the expected structure
                    const formatted = alumniData.map(a => ({
                        id: a.id,
                        name: a.name,
                        year: `Class of ${a.year}`,
                        degree: a.degree,
                        position: a.position || 'Alumni',
                        company: a.company || a.email,
                        image: a.image || '/assets/img/alumni-default.jpg',
                        achievement: a.achievement || 'Distinguished Alumni'
                    }));
                    setFeaturedAlumni(formatted);
                }
            } catch (err) {
                console.error('Failed to fetch featured alumni:', err);
            } finally {
                if (mounted) setFeaturedLoading(false);
            }
        };

        fetchFeaturedAlumni();
        return () => mounted = false;
    }, []);

    // Fetch articles with category_id 3 (events)
    useEffect(() => {
        let mounted = true;
        const fetchEvents = async () => {
            try {
                setEventsLoading(true);
                setEventsError(null);
                const response = await API.get('/articles');
                if (mounted) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
                    
                    // Get articles data from response
                    const articlesData = response.data && Array.isArray(response.data)
                        ? response.data
                        : response.data.articles || response.data.data || [];
                    
                    // Filter articles with category_id 3 and future dates, then sort by date
                    const futureEvents = (Array.isArray(articlesData) ? articlesData : [])
                        .filter(article => article.category_id === 3 && new Date(article.date) >= today)
                        .sort((a, b) => new Date(a.date) - new Date(b.date));
                    
                    setEvents(futureEvents);
                }
            } catch (err) {
                console.error('Failed to fetch events:', err);
                if (mounted) {
                    setEventsError('Failed to load events. Please try again later.');
                }
            } finally {
                if (mounted) setEventsLoading(false);
            }
        };

        fetchEvents();
        return () => mounted = false;
    }, []);

    const getInvolved = [
        {
            icon: Users,
            title: "Join Alumni Association",
            description: "Become an active member and stay connected with your alma mater.",
            action: "Register Now",
            buttonLink: "/alumni/apply"
        },
        {
            icon: GraduationCap,
            title: "Mentor Students",
            description: "Share your experience and guide the next generation of leaders.",
            action: "Become a Mentor",
            buttonLink: "/alumni/mentors/apply"
        },
        {
            icon: Heart,
            title: "Make a Donation",
            description: "Support scholarships and programs that made your education possible.",
            action: "Donate",
            buttonLink: "/donate"
        },
        {
            icon: Briefcase,
            title: "Hire Our Graduates",
            description: "Post job opportunities and recruit talented graduates from ARU.",
            action: "Post a Job",
            buttonLink: "/alumni/jobs/post"
        },
    ];

    const tabs = [
        { id: "alumni", label: "Featured Alumni" },
        { id: "events", label: "Events" },
        { id: "involved", label: "Get Involved" },
    ];

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/alumni';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': 'Alumni - Africa Research University',
        'description': 'Alumni community, events and ways to get involved with Africa Research University.',
        'url': pageUrl
    };

    return (
        <section className="bg-gray-50 min-h-screen pt-1">
            <SEO title="Alumni — Africa Research University" description="Join the ARU alumni community: events, networking and ways to get involved." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
            {/* Hero Section */}
            <Hero
                slides={heroSlides}
                interval={5000}
                height="350px"
                borderRadius="50px"
                hidden={true}
            />

            <div className="container mx-auto px-6 py-16" id="alumni-section">

                {/* Tabs Navigation */}
                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === tab.id
                                ? "bg-green-600 text-white border-green-600 shadow-md"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">

                    {/* Featured Alumni */}
                    {activeTab === "alumni" && (
                        <motion.div
                            key="alumni"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35 }}
                        >
                            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
                                Featured Alumni
                            </h2>
                            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
                                Celebrating the achievements of our distinguished graduates who are making an impact in their fields.
                            </p>
                            {featuredLoading ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Loading featured alumni...</p>
                                </div>
                            ) : featuredAlumni.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-gray-600">No featured alumni at this time.</p>
                                </div>
                            ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                                {featuredAlumni.map((alumni, index) => (
                                    <motion.div
                                        key={alumni.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        data-aos="fade-up"
                                        data-aos-delay={index * 100}
                                        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden"
                                    >
                                        {alumni.image ? (
                                            <div className="h-48 overflow-hidden">
                                                <img 
                                                    src={apiUrl + alumni.image} 
                                                    alt={alumni.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="h-48 bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                                                <GraduationCap className="w-20 h-20 text-white opacity-50" />
                                            </div>
                                        )}
                                        <div className="p-6">
                                            <div className="text-sm text-green-600 font-semibold mb-1">
                                                {alumni.year}
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                                {alumni.name}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-1">{alumni.degree}</p>
                                            <p className="text-sm font-semibold text-gray-700 mb-1">
                                                {alumni.position}
                                            </p>
                                            <p className="text-sm text-gray-600 mb-4">{alumni.company}</p>
                                            <div className="pt-4 border-t border-gray-200">
                                                <p className="text-sm text-gray-700 italic">
                                                    "{alumni.achievement}"
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                            )}
                        </motion.div>
                    )}

                    {/* Events */}
                    {activeTab === "events" && (
                        <motion.div
                            key="events"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35 }}
                        >
                            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
                                Upcoming Events
                            </h2>
                            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
                                Stay connected through our alumni events, reunions, and networking opportunities.
                            </p>
                            <div className="space-y-4 max-w-4xl mx-auto">
                                {eventsLoading ? (
                                    <div className="text-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                                        <p className="mt-4 text-gray-600">Loading events...</p>
                                    </div>
                                ) : eventsError ? (
                                    <div className="text-center py-12">
                                        <div className="text-red-600 mb-2">⚠️</div>
                                        <p className="text-gray-600">{eventsError}</p>
                                        <button 
                                            onClick={() => window.location.reload()} 
                                            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                ) : events.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-600">No upcoming events scheduled.</p>
                                    </div>
                                ) : (
                                    events.map((event, index) => (
                                        <motion.li
                                            key={event.id}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.04 }}
                                            data-aos="fade-up"
                                            data-aos-delay={(index + 1) * 40}
                                            className="bg-white rounded-lg border hover:shadow-md transition overflow-hidden flex items-center gap-4 p-4"
                                        >
                                            <img 
                                                src={apiUrl + event.image} 
                                                alt={event.title} 
                                                className="w-28 h-20 object-cover rounded" 
                                            />

                                            <div className="flex-1">
                                                <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                                                    <span>{event.category_name}</span>
                                                    <span>{dayjs(event.date).format("DD MMM YYYY")}</span>
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    <Link to={`/article/${event.id}`} className="hover:text-green-600">
                                                        {event.title}
                                                    </Link>
                                                </h3>
                                                <div
                                                    className="text-gray-600 text-sm"
                                                    dangerouslySetInnerHTML={{ __html: event.content.substring(0, 100) + '...' }}
                                                />
                                            </div>
                                        </motion.li>
                                    ))
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Get Involved */}
                    {activeTab === "involved" && (
                        <motion.div
                            key="involved"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35 }}
                        >
                            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
                                Get Involved
                            </h2>
                            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
                                There are many ways to stay connected and give back to your university community.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                                {getInvolved.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        data-aos="fade-up"
                                        data-aos-delay={index * 100}
                                        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6 flex flex-col"
                                    >
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                            <item.icon className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                            {item.title}
                                        </h3>
                                        <p className="text-gray-600 mb-6 flex-grow">{item.description}</p>
                                        <a
                                            href={item.buttonLink}
                                            className="w-full px-5 py-3 bg-green-600  text-white rounded-lg font-medium hover:bg-green-700 transition-all"
                                            style={{ textAlign: "center" }}

                                        >
                                            {item.action}
                                        </a>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Contact Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="mt-16 max-w-4xl mx-auto"
                >
                    <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 md:p-12 text-center text-white">
                        <Mail className="w-16 h-16 mx-auto mb-4 opacity-90" />
                        <h2 className="text-3xl font-bold mb-4">Stay Connected</h2>
                        <p className="text-lg mb-6 opacity-90">
                            Update your information and stay in touch with the alumni community.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <button className="px-8 py-3 bg-green-800 text-white rounded-lg font-semibold hover:bg-green-900 transition-all border-2 border-white">
                                Contact Alumni Office
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default AlumniPage;